import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Chip,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import { ApplicationSectionHeader } from '../application/applicationSectionChrome';
import type { IncomeDashboardDetail } from '../../api/incomeVerificationDashboardApi';
import type { IncomeSubTarget } from '../../lib/loanReview/mapLoanReviewToViewModel';
import {
  depositMatchesView,
  depositStatusLabel,
  depositUiStatus,
  INCOME_DEPOSIT_LABELS,
  sumDepositAmounts,
  type IncomeDepositViewMode,
  viewModeLabel,
} from '../../lib/incomeDepositTerminology';
import {
  incomeDepositPayorGroupKey,
  incomeDepositPayorLabel,
  incomeDepositPayorRawCaption,
} from '../../lib/incomeDepositPayorDisplay';
import { derivePayorGroups } from '../../pages/incomeDashboardDerived';
import {
  resolveDepositStreamAccent,
  type IncomeStreamAccentMap,
} from '../../lib/incomeStreamPresentation';
import { accordionSummaryExpandLeftSx } from './journeyAccordionStyles';

type DepositRow = IncomeDashboardDetail['incomeDeposits'][number];
type BankTxRow = IncomeDashboardDetail['bankTransactions'][number];
type ActivityKind = 'credits' | 'debits';

function formatIso(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

function formatCad(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(value);
}

function ColumnHeader({ label, help }: { label: string; help: string }) {
  return (
    <Stack direction="row" spacing={0.35} alignItems="center" component="span">
      <span>{label}</span>
      <Tooltip title={help} arrow placement="top">
        <InfoOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary', cursor: 'help' }} />
      </Tooltip>
    </Stack>
  );
}

type DebitViewMode = 'ALL' | 'INVALID' | 'GAMBLING' | 'BY_INSTITUTION';

function StatusChip({
  row,
  streamAccentMap,
  colorizeFeedsIncome = false,
}: {
  row: DepositRow;
  streamAccentMap?: IncomeStreamAccentMap;
  colorizeFeedsIncome?: boolean;
}) {
  const status = depositUiStatus(row);
  const label = depositStatusLabel(status);
  if (colorizeFeedsIncome && status === 'feeds_income' && streamAccentMap && streamAccentMap.size > 0) {
    const accent = resolveDepositStreamAccent(row, streamAccentMap);
    if (accent) {
      return (
        <Chip
          size="small"
          label={label}
          variant="filled"
          sx={{ bgcolor: accent.main, color: '#fff', fontWeight: 700 }}
        />
      );
    }
  }
  const color =
    status === 'feeds_income' ? 'primary' : status === 'excluded' ? 'warning' : status === 'invalid' ? 'error' : 'default';
  return <Chip size="small" label={label} color={color} variant={status === 'feeds_income' ? 'filled' : 'outlined'} />;
}

function DebitStatusChip({ row }: { row: BankTxRow }) {
  if (row.isInvalid) {
    return <Chip size="small" label={INCOME_DEPOSIT_LABELS.statusInvalid} color="error" variant="filled" />;
  }
  return <Chip size="small" label={INCOME_DEPOSIT_LABELS.statusDebit} color="primary" variant="filled" />;
}

/**
 * Present only when ATA matched this row back to a stored adverse group. Absent
 * does not mean "checked and clean" - see BankTxRow.adverseCategory.
 */
function AdverseCategoryChip({ row }: { row: BankTxRow }) {
  if (!row.adverseCategory) return null;
  const label = row.adverseMerchant
    ? `${row.adverseCategory} · ${row.adverseMerchant}`
    : row.adverseCategory;
  return <Chip size="small" label={label} color="error" variant="outlined" sx={{ ml: 0.5 }} />;
}

function sumDebitAmounts(rows: BankTxRow[]): number {
  let sum = 0;
  for (const t of rows) {
    const amt = t.amount ?? t.debit;
    if (amt != null && Number.isFinite(amt)) sum += amt;
  }
  return sum;
}

function debitInstitutionKey(t: BankTxRow): string {
  return (t.institutionName ?? t.institutionCode ?? '—').trim() || '—';
}

function CreditTable({
  rows,
  maxHeight,
  streamAccentMap,
  colorizeFeedsIncome = false,
}: {
  rows: DepositRow[];
  maxHeight?: number | string;
  streamAccentMap?: IncomeStreamAccentMap;
  colorizeFeedsIncome?: boolean;
}) {
  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        No credits in this view.
      </Typography>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: maxHeight ?? 400 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>{INCOME_DEPOSIT_LABELS.colCreditDate}</TableCell>
            <TableCell align="right">{INCOME_DEPOSIT_LABELS.colAmount}</TableCell>
            <TableCell>{INCOME_DEPOSIT_LABELS.colPayor}</TableCell>
            <TableCell>
              <ColumnHeader label={INCOME_DEPOSIT_LABELS.colStatus} help={INCOME_DEPOSIT_LABELS.colStatusHelp} />
            </TableCell>
            <TableCell>{INCOME_DEPOSIT_LABELS.colExclusionReason}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[...rows]
            .sort((a, b) => new Date(b.depositDate).getTime() - new Date(a.depositDate).getTime())
            .map((d) => {
              const status = depositUiStatus(d);
              const payorRawCaption = incomeDepositPayorRawCaption(d);
              const reason =
                status === 'excluded'
                  ? d.exclusionReason ?? '—'
                  : status === 'invalid'
                    ? d.invalidReason ?? '—'
                    : '—';
              return (
                <TableRow key={d.id} hover selected={d.usedInModel}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatIso(d.depositDate)}</TableCell>
                  <TableCell align="right">{formatCad(d.amount)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{incomeDepositPayorLabel(d)}</Typography>
                    {payorRawCaption ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
                        {payorRawCaption}
                      </Typography>
                    ) : null}
                    <Typography variant="caption" color="text.secondary">
                      {d.payorType}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <StatusChip
                      row={d}
                      streamAccentMap={streamAccentMap}
                      colorizeFeedsIncome={colorizeFeedsIncome}
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 260, wordBreak: 'break-word' }}>{reason}</TableCell>
                </TableRow>
              );
            })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function DebitTable({ rows, maxHeight }: { rows: BankTxRow[]; maxHeight?: number | string }) {
  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        No debits in this view.
      </Typography>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: maxHeight ?? 400 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>{INCOME_DEPOSIT_LABELS.colDebitDate}</TableCell>
            <TableCell align="right">{INCOME_DEPOSIT_LABELS.colAmount}</TableCell>
            <TableCell>{INCOME_DEPOSIT_LABELS.colDescription}</TableCell>
            <TableCell>
              <ColumnHeader label={INCOME_DEPOSIT_LABELS.colStatus} help={INCOME_DEPOSIT_LABELS.colDebitStatusHelp} />
            </TableCell>
            <TableCell>{INCOME_DEPOSIT_LABELS.colExclusionReason}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[...rows]
            .sort((a, b) => new Date(b.postedAt).getTime() - new Date(a.postedAt).getTime())
            .map((t) => (
              <TableRow key={t.id} hover>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatIso(t.postedAt)}</TableCell>
                <TableCell align="right">{formatCad(t.amount ?? t.debit)}</TableCell>
                <TableCell sx={{ maxWidth: 320, wordBreak: 'break-word' }}>
                  <Typography variant="body2">{t.summaryLine || t.description || '—'}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t.institutionName ?? t.institutionCode ?? '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                    <DebitStatusChip row={t} />
                    <AdverseCategoryChip row={t} />
                  </Stack>
                </TableCell>
                <TableCell sx={{ maxWidth: 260, wordBreak: 'break-word' }}>
                  {t.isInvalid ? t.invalidReason ?? '—' : '—'}
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function IncomeDepositEvidencePanel({
  data,
  initialView = 'IN_MONTHLY_INCOME',
  embedded = false,
  variant = 'default',
  streamAccentMap,
  incomeJump,
}: {
  data: Pick<
    IncomeDashboardDetail,
    'id' | 'finalStatus' | 'verdict' | 'metrics' | 'incomeDeposits' | 'bankTransactions' | 'transactionMeta'
  >;
  initialView?: IncomeDepositViewMode;
  embedded?: boolean;
  variant?: 'default' | 'modal' | 'embedded';
  streamAccentMap?: IncomeStreamAccentMap;
  /** Opens directly to a specific sub-view, e.g. from a hero "see Income tab" bullet. */
  incomeJump?: { target: IncomeSubTarget; nonce: number } | null;
}) {
  const isEmbedded = embedded || variant === 'embedded';
  const deposits = data.incomeDeposits ?? [];
  const bankTxs = data.bankTransactions ?? [];
  const [activity, setActivity] = useState<ActivityKind>('credits');
  const [view, setView] = useState<IncomeDepositViewMode>(initialView);
  const [debitView, setDebitView] = useState<DebitViewMode>('ALL');
  const [expandedPayor, setExpandedPayor] = useState<string | false>(false);
  const [expandedInstitution, setExpandedInstitution] = useState<string | false>(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setView(initialView);
  }, [initialView]);

  useEffect(() => {
    if (!incomeJump) return;
    if (incomeJump.target === 'gambling-debits') {
      setActivity('debits');
      setDebitView('GAMBLING');
      // A hero bullet's tab-switch already scrolls the whole tab panel into
      // view; this scrolls the specific card the reviewer actually needs,
      // one animation frame later so the tab-panel scroll settles first.
      requestAnimationFrame(() => {
        rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
    // Depends on nonce so re-clicking the same bullet re-jumps even when the
    // target hasn't changed - mirrors jumpToCrossCheck's jumpNonce.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomeJump?.nonce]);

  const feedsIncomeDeposits = useMemo(() => deposits.filter((d) => d.usedInModel), [deposits]);
  const debits = useMemo(() => bankTxs.filter((t) => t.direction === 'DEBIT'), [bankTxs]);
  const debitCounts = useMemo(() => {
    let invalid = 0;
    for (const t of debits) {
      if (t.isInvalid) invalid += 1;
    }
    const gambling = debits.reduce((n, t) => n + (t.adverseCategory === 'GAMBLING' ? 1 : 0), 0);
    return { all: debits.length, invalid, valid: debits.length - invalid, gambling };
  }, [debits]);
  const filteredDebits = useMemo(() => {
    if (debitView === 'INVALID') return debits.filter((t) => t.isInvalid);
    if (debitView === 'GAMBLING') return debits.filter((t) => t.adverseCategory === 'GAMBLING');
    return debits;
  }, [debits, debitView]);
  const institutionGroups = useMemo(() => {
    const map = new Map<string, BankTxRow[]>();
    for (const t of filteredDebits) {
      const key = debitInstitutionKey(t);
      const list = map.get(key);
      if (list) list.push(t);
      else map.set(key, [t]);
    }
    return [...map.entries()]
      .map(([key, rows]) => ({
        key,
        displayName: key,
        debits: rows,
        sum: sumDebitAmounts(rows),
      }))
      .sort((a, b) => b.debits.length - a.debits.length || a.displayName.localeCompare(b.displayName));
  }, [filteredDebits]);

  const reconciliation = useMemo(() => {
    const countFeeds = feedsIncomeDeposits.length;
    return { countFeeds };
  }, [feedsIncomeDeposits.length]);

  const usesAuditExclusions = deposits.some((d) => d.excludedByAudit === true);

  const counts = useMemo(() => {
    let feeds = 0;
    let excluded = 0;
    let analysisOnly = 0;
    let invalid = 0;
    for (const d of deposits) {
      const s = depositUiStatus(d);
      if (s === 'feeds_income') feeds += 1;
      else if (s === 'excluded') excluded += 1;
      else if (s === 'invalid') invalid += 1;
      else analysisOnly += 1;
    }
    return { feeds, excluded, analysisOnly, invalid, all: deposits.length };
  }, [deposits]);

  const filtered = useMemo(
    () => deposits.filter((d) => depositMatchesView(d, view)),
    [deposits, view],
  );

  const payorGroups = useMemo(() => {
    const groups = derivePayorGroups(deposits);
    const ids = new Set(filtered.map((d) => d.id));
    return groups
      .map((g) => ({
        ...g,
        deposits: deposits.filter((d) => ids.has(d.id) && incomeDepositPayorGroupKey(d) === g.key),
      }))
      .filter((g) => g.deposits.length > 0);
  }, [deposits, filtered]);

  const tableMaxHeight = variant === 'modal' ? 'calc(100vh - 24rem)' : isEmbedded ? 360 : 400;
  const hideTitle = isEmbedded || variant === 'modal';

  const hasAnyActivity = deposits.length > 0 || bankTxs.length > 0;
  if (!hasAnyActivity) {
    return (
      <Alert severity="info">No bank credits or debits are stored for this verification session.</Alert>
    );
  }

  const creditTabs: IncomeDepositViewMode[] = [
    'IN_MONTHLY_INCOME',
    'EXCLUDED',
    'INCLUDED',
    'BY_PAYOR',
    'ALL',
  ];

  return (
    <Stack spacing={1.5} ref={rootRef}>
      {!hideTitle ? (
        <ApplicationSectionHeader
          title={INCOME_DEPOSIT_LABELS.sectionTitle}
          subtitle={INCOME_DEPOSIT_LABELS.sectionIntro}
          icon={<ReceiptLongRoundedIcon fontSize="small" />}
        />
      ) : null}

      <Tabs
        value={activity}
        onChange={(_, v: ActivityKind) => setActivity(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 40 }}
      >
        <Tab
          value="credits"
          label={`${INCOME_DEPOSIT_LABELS.activityCredits} (${counts.all})`}
          sx={{ textTransform: 'none', fontWeight: 700, minHeight: 40 }}
        />
        <Tab
          value="debits"
          label={`${INCOME_DEPOSIT_LABELS.activityDebits} (${debits.length})`}
          sx={{ textTransform: 'none', fontWeight: 700, minHeight: 40 }}
        />
      </Tabs>

      {activity === 'credits' ? (
        <Stack spacing={1.5}>
          {deposits.length === 0 ? (
            <Alert severity="info">No bank credits are stored for this verification session.</Alert>
          ) : (
            <>
              {usesAuditExclusions && !isEmbedded ? (
                <Alert severity="info" sx={{ py: 0.75 }}>
                  Excluded tab shows only credits listed in <strong>BankIncomeAudit.outlierDetails.excluded</strong>.
                </Alert>
              ) : null}

              <Tabs
                value={view}
                onChange={(_, v) => setView(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 36 }}
              >
                {creditTabs.map((mode) => {
                  let count = counts.all;
                  if (mode === 'IN_MONTHLY_INCOME') count = counts.feeds;
                  else if (mode === 'EXCLUDED') count = counts.excluded;
                  else if (mode === 'INCLUDED') count = counts.analysisOnly;
                  return (
                    <Tab
                      key={mode}
                      value={mode}
                      label={`${viewModeLabel(mode)} (${count})`}
                      sx={{ textTransform: 'none', fontWeight: 700, minHeight: 36, py: 0.75 }}
                    />
                  );
                })}
              </Tabs>

              {view === 'IN_MONTHLY_INCOME' && reconciliation.countFeeds > 0 ? (
                <Typography variant="caption" color="text.secondary">
                  Tab total: <strong>{formatCad(sumDepositAmounts(filtered))}</strong> across {filtered.length}{' '}
                  credit{filtered.length === 1 ? '' : 's'}.
                </Typography>
              ) : null}

              {data.transactionMeta?.incomeDepositsCapped ? (
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  Showing the most recent {data.transactionMeta.incomeDepositsReturned} credits (limit).
                </Alert>
              ) : null}

              {view === 'BY_PAYOR' ? (
                <Stack spacing={1}>
                  {payorGroups.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No credits in this view.
                    </Typography>
                  ) : (
                    payorGroups.map((group) => (
                      <Accordion
                        key={group.key}
                        expanded={expandedPayor === group.key}
                        onChange={(_, exp) => setExpandedPayor(exp ? group.key : false)}
                        disableGutters
                        sx={{ border: 1, borderColor: 'divider', '&:before': { display: 'none' } }}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={accordionSummaryExpandLeftSx}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography variant="body2" fontWeight={700}>
                              {group.displayName}
                            </Typography>
                            <Chip size="small" label={`${group.deposits.length} credits`} variant="outlined" />
                            <Chip
                              size="small"
                              label={`Used for income ${formatCad(sumDepositAmounts(group.deposits, { usedInModelOnly: true }))}`}
                              color="primary"
                              variant="outlined"
                            />
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                          <CreditTable
                            rows={group.deposits}
                            maxHeight={tableMaxHeight}
                            streamAccentMap={streamAccentMap}
                            colorizeFeedsIncome={false}
                          />
                        </AccordionDetails>
                      </Accordion>
                    ))
                  )}
                </Stack>
              ) : (
                <CreditTable
                  rows={filtered}
                  maxHeight={tableMaxHeight}
                  streamAccentMap={streamAccentMap}
                  colorizeFeedsIncome={view === 'IN_MONTHLY_INCOME'}
                />
              )}
            </>
          )}
        </Stack>
      ) : (
        <Stack spacing={1.5}>
          {debits.length === 0 ? (
            <Alert severity="info">No debit transactions are stored for this session.</Alert>
          ) : (
            <>
              <Tabs
                value={debitView}
                onChange={(_, v: DebitViewMode) => setDebitView(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 36 }}
              >
                <Tab
                  value="ALL"
                  label={`${INCOME_DEPOSIT_LABELS.tabAllDebits} (${debitCounts.all})`}
                  sx={{ textTransform: 'none', fontWeight: 700, minHeight: 36, py: 0.75 }}
                />
                <Tab
                  value="INVALID"
                  label={`${INCOME_DEPOSIT_LABELS.tabInvalidDebits} (${debitCounts.invalid})`}
                  sx={{ textTransform: 'none', fontWeight: 700, minHeight: 36, py: 0.75 }}
                />
                {/* Also shown while selected with no rows, so a deep link to it lands on
                    a highlighted tab with an explanation instead of an invalid value. */}
                {debitCounts.gambling > 0 || debitView === 'GAMBLING' ? (
                  <Tab
                    value="GAMBLING"
                    label={`Gambling (${debitCounts.gambling})`}
                    sx={{ textTransform: 'none', fontWeight: 700, minHeight: 36, py: 0.75, color: 'error.main' }}
                  />
                ) : null}
                <Tab
                  value="BY_INSTITUTION"
                  label={`${INCOME_DEPOSIT_LABELS.tabByInstitution} (${debitCounts.all})`}
                  sx={{ textTransform: 'none', fontWeight: 700, minHeight: 36, py: 0.75 }}
                />
              </Tabs>

              {filteredDebits.length > 0 ? (
                <Typography variant="caption" color="text.secondary">
                  Tab total: <strong>{formatCad(sumDebitAmounts(filteredDebits))}</strong> across{' '}
                  {filteredDebits.length} debit{filteredDebits.length === 1 ? '' : 's'}.
                </Typography>
              ) : null}

              {debitView === 'GAMBLING' && debitCounts.gambling === 0 ? (
                <Alert severity="info" sx={{ py: 0.5 }}>
                  Gambling was flagged for this application, but no individual debit in the stored
                  transactions could be matched to it — for example on an older run, or when the
                  matching rows fall outside the stored transaction limit. Review All debits.
                </Alert>
              ) : null}

              {data.transactionMeta?.bankTransactionsCapped ? (
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  Showing the most recent {data.transactionMeta.bankTransactionsReturned} bank transactions (limit).
                </Alert>
              ) : null}

              {debitView === 'BY_INSTITUTION' ? (
                <Stack spacing={1}>
                  {institutionGroups.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No debits in this view.
                    </Typography>
                  ) : (
                    institutionGroups.map((group) => (
                      <Accordion
                        key={group.key}
                        expanded={expandedInstitution === group.key}
                        onChange={(_, exp) => setExpandedInstitution(exp ? group.key : false)}
                        disableGutters
                        sx={{ border: 1, borderColor: 'divider', '&:before': { display: 'none' } }}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={accordionSummaryExpandLeftSx}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography variant="body2" fontWeight={700}>
                              {group.displayName}
                            </Typography>
                            <Chip size="small" label={`${group.debits.length} debits`} variant="outlined" />
                            <Chip
                              size="small"
                              label={formatCad(group.sum)}
                              color="primary"
                              variant="outlined"
                            />
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                          <DebitTable rows={group.debits} maxHeight={tableMaxHeight} />
                        </AccordionDetails>
                      </Accordion>
                    ))
                  )}
                </Stack>
              ) : (
                <DebitTable rows={filteredDebits} maxHeight={tableMaxHeight} />
              )}
            </>
          )}
        </Stack>
      )}
    </Stack>
  );
}
